import React, { useEffect, useState } from 'react';
import { Button } from './button';
import { Text } from './text';
import { Dialog, DialogTitle, DialogBody, DialogActions } from './dialog';
import { ArchiveBoxIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { archivarFianza } from '../api/comisiones';
import { formatNumeroFianza } from '../utils/calculations';

// Archivar = borrado lógico. Before the broker confirms, the backend answers
// (dryRun) exactly what will stop counting: the fianza, its ledger movements
// and the requerida that reopens. If any comisión was already cobrada or paid
// out to an asociado the archive is blocked here, with each cobro listed, and
// the owner undoes those in the libro first.

const money = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(n) || 0);

const fecha = (ms) => {
  if (!ms) return '';
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('es-MX');
};

const TIPO_LABELS = {
  emision: 'Emisión',
  adicional: 'Prima adicional',
  sustituye: 'Sustitución de prima',
  cargo: 'Cargo',
};

const ESTADO_COBRO_LABELS = {
  pendiente: 'Pendiente',
  cobrada: 'Cobrada',
  neutralizada: 'Neutralizada',
  adeudo: 'Adeudo',
  adeudo_liquidado: 'Adeudo liquidado',
};

export function ArchivarFianzaDialog({ open, onClose, contratoId, fianza, onArchived }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);

  const fianzaId = fianza?.id;

  useEffect(() => {
    if (!open || !contratoId || !fianzaId) return undefined;
    let cancelled = false;
    setPreview(null);
    setError(null);
    setLoading(true);
    archivarFianza({ contractId: contratoId, fianzaId, dryRun: true })
      .then((res) => { if (!cancelled) setPreview(res); })
      .catch((err) => { if (!cancelled) setError(err.message || 'No se pudo consultar el libro.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, contratoId, fianzaId]);

  const handleConfirm = async () => {
    if (!contratoId || !fianzaId) return;
    setWorking(true);
    setError(null);
    try {
      const res = await archivarFianza({ contractId: contratoId, fianzaId });
      if (onArchived) onArchived(res);
    } catch (err) {
      if (err.code === 'ARCHIVO_BLOQUEADO') {
        setPreview({ puedeArchivar: false, bloqueos: err.bloqueos || [], resumen: err.resumen || null });
      }
      setError(err.message || 'No se pudo archivar la fianza.');
    } finally {
      setWorking(false);
    }
  };

  const bloqueos = preview?.bloqueos || [];
  const resumen = preview?.resumen || null;
  const bloqueada = bloqueos.length > 0;
  const numero = formatNumeroFianza(fianza || {}, 'Sin número');

  return (
    <Dialog open={open} onClose={() => !working && onClose()} size="md">
      <DialogTitle className="flex items-center gap-2">
        <ArchiveBoxIcon className="h-5 w-5 text-azul-surelia" />
        Archivar fianza {numero}
      </DialogTitle>
      <DialogBody className="space-y-4">
        {loading && (
          <div className="flex items-center gap-3 py-6 text-sm text-zinc-500">
            <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-azul-surelia" />
            Consultando el libro de comisiones
          </div>
        )}

        {!loading && error && !bloqueada && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
        )}

        {!loading && preview && bloqueada && (
          <div className="space-y-3">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex gap-3">
              <LockClosedIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-semibold">Esta fianza tiene dinero ya cobrado</p>
                <p className="mt-1">
                  Deshaz estos cobros en Comisiones (o el pago del asociado en Asociados) y vuelve a intentar.
                </p>
              </div>
            </div>
            <ul className="divide-y divide-zinc-200 rounded-lg border border-zinc-200 bg-white">
              {bloqueos.map((b, i) => (
                <li key={`${b.movId}-${b.tipo}-${i}`} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <div className="font-medium text-zinc-800">
                      {b.tipo === 'reparto_pagado'
                        ? `Reparto pagado a ${b.asociadoNombre || 'asociado'}`
                        : `Comisión ${ESTADO_COBRO_LABELS[b.estadoCobro] || 'cobrada'}`}
                    </div>
                    <div className="text-xs text-zinc-500">
                      {b.tipo === 'reparto_pagado'
                        ? `Periodo ${b.periodo || ''}`
                        : [TIPO_LABELS[b.tipoMovimiento] || b.tipoMovimiento, fecha(b.fecha)].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <div className="font-semibold tabular-nums text-zinc-800 whitespace-nowrap">
                    {money(b.tipo === 'reparto_pagado' ? b.monto : b.comisionMonto)}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {!loading && preview && !bloqueada && (
          <div className="space-y-3">
            <Text>
              La fianza deja de contar en todos lados: listas, Pulso, reportes, pendientes y el portal del cliente.
              Sus archivos se conservan y se puede restaurar desde Clientes, Archivados.
            </Text>

            <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-zinc-600">Movimientos de comisión que salen del libro</span>
                <span className="font-semibold tabular-nums">{resumen?.movimientos ?? 0}</span>
              </div>
              {(resumen?.movimientos ?? 0) > 0 && (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-600">Comisión neta que deja de contar</span>
                    <span className="font-semibold tabular-nums">{money(resumen.comisionNeta)}</span>
                  </div>
                  {(resumen.porAgente || []).length > 1 && (
                    <ul className="pl-3 space-y-1">
                      {resumen.porAgente.map((a) => (
                        <li key={a.agente} className="flex items-center justify-between text-xs text-zinc-600">
                          <span className="truncate">{a.agente}</span>
                          <span className="tabular-nums">{money(a.comision)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <ul className="divide-y divide-zinc-200 rounded border border-zinc-200 bg-white">
                    {(resumen.detalle || []).map((m) => (
                      <li key={m.movId} className="flex items-center justify-between gap-3 px-2.5 py-1.5 text-xs">
                        <span className="text-zinc-700">
                          {TIPO_LABELS[m.tipo] || m.tipo}
                          {m.fecha ? ` · ${fecha(m.fecha)}` : ''}
                          {' · '}
                          {ESTADO_COBRO_LABELS[m.estadoCobro] || m.estadoCobro || ''}
                        </span>
                        <span className="tabular-nums font-medium text-zinc-800">{money(m.comisionMonto)}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {resumen?.requeridaReabre && (
                <div className="flex items-center justify-between">
                  <span className="text-zinc-600">Fianza requerida del contrato</span>
                  <span className="font-semibold">Vuelve a pendiente</span>
                </div>
              )}
            </div>
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
            )}
          </div>
        )}
      </DialogBody>
      <DialogActions>
        <Button outline color="surelia" onClick={onClose} disabled={working}>
          {bloqueada ? 'Cerrar' : 'Cancelar'}
        </Button>
        {!bloqueada && (
          <Button
            color="surelia"
            onClick={handleConfirm}
            disabled={working || loading || !preview || !preview.puedeArchivar}
          >
            {working ? 'Archivando...' : 'Archivar fianza'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ArchivarFianzaDialog;
